import i18n from 'services/i18n'
import htmlToFormattedText from 'html-to-formatted-text'
import {
    fontNunitoBold,
    fontNunitoItalic,
    fontNunitoNormal,
    fontQuicksandBold
} from 'assets/fonts/Nunito-Fonts-Base64.js'
import { fontIcomoonNormal } from 'assets/fonts/icomoon-normal.js'
import { formattedDateForCountry } from 'components/shared/mixins/dateHelper'

import jsPDF from 'jspdf'
import config from 'services/config'
import he from 'he'
import store from 'store'

const certificateBaseImageSingle = require('@/assets/js/certificateBaseSingle')
const linestartForBottomItems = 12
const marginBetweenLabelAndValue = 2
let startCoordinateForAttributes = 212

function resetStartCoordinate () {
    startCoordinateForAttributes = 212
}

function setCertificateLabel (certificate) {
    certificate.setFontSize(38)
    certificate.setFont('Quicksand-Bold')
    certificate.text(13, 43, i18n.t('Certificates.certificate_title'))
    return certificate
}

function setCompletedOnDate (certificate, course) {
    certificate.setFont('Nunito-Regular')
    certificate.setFontSize(14)
    certificate.text(170, 18, i18n.t('Certificates.completed_on'))
    certificate.text(170, 25, formattedDateForCountry(course.date))
    return certificate
}

function setCourseTitle (certificate, course) {
    certificate.setFont('Nunito-Regular')
    certificate.setFontSize(16)
    let splitTitle = ''
    if (isLearningPath(course)) {
        splitTitle = certificate.splitTextToSize(course.learningPathName, 160)
    } else {
        splitTitle = certificate.splitTextToSize(course.courseTitle, 160)
    }
    certificate.text(30, 110, splitTitle)
    return certificate
}

function setCourseDescription (certificate, course) {
    certificate.setFontSize(11)
    certificate.setFont('Nunito-Regular')
    let description = ''
    if (isLearningPath(course)) {
        description = truncate(course.learningPathDescription, 600)
    } else {
        description = truncate(course.courseDescription, 600)
    }
    let parser = new DOMParser()
    const document = parser.parseFromString(description, 'text/html')
    let links = document.getElementsByTagName('a')

    Array.prototype.slice.call(links).forEach((link) => {
        link.innerHTML = ' '
    })

    let cleanedDescription = document.body.innerHTML
    cleanedDescription = he.decode(cleanedDescription)
    let htmlFormattedDesc = htmlToFormattedText(cleanedDescription)
    let splitDescription = certificate.splitTextToSize(htmlFormattedDesc, 160)

    certificate.text(30, 135, splitDescription.slice(0, 11))
    return certificate
}

function setUserNameHasCompletedCourse (certificate, course) {
    certificate.setFontSize(28)
    certificate.setFont('Nunito-Regular')
    let fullName = course.userName
    certificate.text(fullName, 105, 80, 'center')
    certificate.setFontSize(21)

    if (course.courseIds !== undefined) {
        certificate.setFont('icomoon')
        certificate.setTextColor('#e47700')
        certificate.setFontSize(27)
        certificate.text('', 105, 68, 'center')
        certificate.setTextColor('black')
        certificate.setFont('Nunito-Regular')
        certificate.setFontSize(21)
        certificate.text(i18n.t('Certificates.has_completed_learning_path'), 105, 89, 'center')
    } else {
        certificate.setTextColor('black')
        certificate.setFont('Nunito-Regular')
        certificate.setFontSize(21)
        certificate.text(i18n.t('Certificates.has_completed_course'), 105, 89, 'center')
    }
    return certificate
}

function setCourseInstructor (certificate, course) {
    if (course.instructors?.length === 0) {
        return certificate
    }
    certificate.setFontSize(12)
    certificate.setFont('Nunito-Regular')
    certificate.setTextColor('black')
    certificate.text(i18n.t('Certificates.instructor'), linestartForBottomItems, startCoordinateForAttributes)
    certificate.setTextColor('#e47700')
    const instructorsNameString = course.instructors.map(instructor => instructor.name).join(', ')

    let splitInstructor = certificate.splitTextToSize(instructorsNameString, 130)

    if (course.instructor !== null) {
        certificate.text(getLineCoordinateForAttribute(certificate, i18n.t('Certificates.instructor')), startCoordinateForAttributes, splitInstructor)
    }
    if (splitInstructor.length > 1) {
        startCoordinateForAttributes += 4
    }
    startCoordinateForAttributes += 8
    return certificate
}

function setCoursePublishDate (certificate, course, isLearningPath = false) {
    certificate.setFontSize(12)
    certificate.setFont('Nunito-Regular')
    certificate.setTextColor('black')
    certificate.text(i18n.t('Certificates.published_date'), linestartForBottomItems, startCoordinateForAttributes)
    certificate.setTextColor('#e47700')
    if (course.coursePublishDate !== null && !isLearningPath) {
        certificate.text(getLineCoordinateForAttribute(certificate, i18n.t('Certificates.published_date')), startCoordinateForAttributes, formattedDateForCountry(course.coursePublishDate))
    } else if (course.learningPathPublishDate !== null && isLearningPath) {
        certificate.text(getLineCoordinateForAttribute(certificate, i18n.t('Certificates.published_date')), startCoordinateForAttributes, formattedDateForCountry(course.learningPathPublishDate))
    } else {
        certificate.text(getLineCoordinateForAttribute(certificate, i18n.t('Certificates.published_date')), startCoordinateForAttributes, '-')
    }
    startCoordinateForAttributes += 8
    return certificate
}

function setCourseCategories (certificate, course, isLearningPath = false) {
    certificate.setFontSize(12)
    certificate.setFont('Nunito-Regular')
    certificate.setTextColor('black')
    certificate.text(i18n.t('Certificates.categories'), linestartForBottomItems, startCoordinateForAttributes)
    certificate.setTextColor('#e47700')
    let categoriesString = ''
    categoriesString = [...new Set(course.categories.map(item => item.label))].join(', ')
    let splitCategories = certificate.splitTextToSize(categoriesString, 160)
    certificate.text(getLineCoordinateForAttribute(certificate, i18n.t('Certificates.categories')), startCoordinateForAttributes, splitCategories)
    categoriesString.length > 80 ? startCoordinateForAttributes += 12 : startCoordinateForAttributes += 8
    return certificate
}

function setCourseDuration (certificate, course, unitForHour) {
    certificate.setFontSize(12)
    certificate.setFont('Nunito-Regular')
    certificate.setTextColor('black')
    certificate.text(i18n.t('Certificates.duration'), linestartForBottomItems, startCoordinateForAttributes)
    certificate.setTextColor('#e47700')
    certificate.text(getLineCoordinateForAttribute(certificate, i18n.t('Certificates.duration')), startCoordinateForAttributes, convertMinutesToHours(course.duration, unitForHour))
    startCoordinateForAttributes += 8
    return certificate
}

function setCourseLevel (certificate, course) {
    if (course.level === undefined) {
        return certificate
    }
    certificate.setFontSize(12)
    certificate.setFont('Nunito-Regular')
    certificate.setTextColor('black')
    certificate.text(i18n.t('Certificates.level'), linestartForBottomItems, startCoordinateForAttributes)
    certificate.setTextColor('#e47700')
    certificate.text(getLineCoordinateForAttribute(certificate, i18n.t('Certificates.level')), startCoordinateForAttributes, i18n.t(`Select.skill_level_${course.level}`))
    startCoordinateForAttributes += 8
    return certificate
}

function printCertificateAuthorizationsSE (certificate, categoryCredits) {
    const categoriesCount = Object.keys(categoryCredits).length
    let authorizationLabelY = 270 - ((categoriesCount - 1) * 6)
    let authorizationLabel = ''
    certificate.setFontSize(12)
    certificate.setTextColor('black')
    // eslint-disable-next-line no-unused-vars
    for (const category in categoryCredits) {
        switch (category.toLowerCase()) {
        case 'aktualitetstimmar redovisningskonsult: redovisning':
            authorizationLabel = i18n.t('Certificates.authorization_hours_accounting_total_start') + ' ' + categoryCredits[category] + ' ' + i18n.t('Certificates.authorization_hours_accounting_total_end')
            break
        case 'aktualitetstimmar redovisningskonsult: skatt':
            authorizationLabel = i18n.t('Certificates.authorization_hours_tax_vat_total_start') + ' ' + categoryCredits[category] + ' ' + i18n.t('Certificates.authorization_hours_tax_vat_total_end')
            break
        case 'lön':
        case 'aktualitetstimmar lönekonsult: lön':
            authorizationLabel = i18n.t('Certificates.authorization_hours_lon_total_start') + ' ' + categoryCredits[category] + ' ' + i18n.t('Certificates.authorization_hours_lon_total_end')
            break
        }
        authorizationLabelY += 6
        certificate.text(12, authorizationLabelY, authorizationLabel)
    }
}

function setAuthorizations (certificate, course, isLearningPath = false) {
    let creditsByCategory = {}
    if (isLearningPath) {
        creditsByCategory = getCreditsByCategoryForLearningPath(course)
    } else {
        creditsByCategory = getCreditsByCategoryForSingleCourse(course)
    }
    switch (config.appLanguage) {
    case 'fi':
        printCertificateAuthorizationsFI(certificate, creditsByCategory)
        break
    case 'se':
        if (course.authorizationId) {
            certificate.setTextColor('black')
            const label = 'SRF ID: '
            certificate.text(12, startCoordinateForAttributes, label)
            certificate.text(getLineCoordinateForAttribute(certificate, label), startCoordinateForAttributes, course.authorizationId)
            certificate.setTextColor('#e47700')
        }
        printCertificateAuthorizationsSE(certificate, creditsByCategory)
        break
    case 'no':
        printCertificateAuthorizationsNO(certificate, creditsByCategory)
        break
    }
    return certificate
}

function printCertificateAuthorizationsNO (certificate, categoryCredits) {
    let authorizationLabelY = 266
    let authorizationLabelX = 12
    certificate.setTextColor('black')
    certificate.setFontSize(12)

    certificate.setFont('Quicksand-Bold')
    certificate.text(12, 260, i18n.t('Certificates_no_authorization_info_label'))
    certificate.setFont('Nunito-Regular')
    for (const category in categoryCredits) {
        certificate.text(authorizationLabelX, authorizationLabelY, category + ': ' + categoryCredits[category] + ' t')
        authorizationLabelY += 6
        if (authorizationLabelY >= 283) {
            authorizationLabelY = 266
            authorizationLabelX += 96
        }
    }
}

function getCreditsByCategoryForLearningPath (learningPath) {
    return learningPath.courseCertificates.reduce((acc, courseCertificate) => {
        courseCertificate.durations.forEach((item) => {
            acc[item.category] = (acc[item.category] || 0) + item.credits
        })
        return acc
    }, {})
}

function getCreditsByCategoryForSingleCourse (courseCertificate) {
    return courseCertificate.durations.reduce((acc, item) => {
        acc[item.category] = item.credits
        return acc
    }, {})
}

function printCertificateAuthorizationsFI (certificate, creditsByCategory) {
    const categoriesCount = Object.keys(creditsByCategory).length
    let authorizationLabelY = 266 - ((categoriesCount - 1) * 5)
    certificate.setFontSize(11)

    // eslint-disable-next-line no-unused-vars
    for (const [category, credits] of Object.entries(creditsByCategory)) {
        certificate.setTextColor('black')
        const authorizationLabel = i18n.t('CertificateList.authorization_summary', { authorizationName: category })
        certificate.text(12, authorizationLabelY, authorizationLabel)
        certificate.setTextColor('#e47700')
        certificate.text(getLineCoordinateForAttribute(certificate, authorizationLabel), authorizationLabelY, Number(credits / 6).toFixed(2))
        authorizationLabelY += 5
    }
}

function setContactInfo (certificate) {
    certificate.setFontSize(14)
    certificate.setTextColor('white')
    certificate.setFont('Nunito-Regular')
    switch (config.appLanguage) {
    case 'fi':
        certificate.text(80, 288, i18n.t('Certificates.email'))
        certificate.text(80, 294, i18n.t('Certificates.phone'))
        certificate.text(154, 288, i18n.t('Certificates.address'))
        certificate.text(154, 294, i18n.t('Certificates.post'))
        break
    case 'se':
        certificate.text(84, 291, i18n.t('Certificates.email'))
        certificate.text(154, 288, i18n.t('Certificates.address'))
        certificate.text(154, 294, i18n.t('Certificates.post'))
        break
    case 'no':
        certificate.text(80, 288, i18n.t('Certificates.email'))
        certificate.text(80, 294, i18n.t('Certificates.phone'))
        certificate.text(154, 288, i18n.t('Certificates.address'))
        certificate.text(154, 294, i18n.t('Certificates.post'))
        break
    }
    return certificate
}

function setCourseType (certificate, course, isLearningPath = false) {
    certificate.setFontSize(12)
    certificate.setFont('Nunito-Regular')
    certificate.setTextColor('black')
    if (isLearningPath) {
        certificate.text(i18n.t('Certificates.course_format'), linestartForBottomItems, startCoordinateForAttributes)
        certificate.setTextColor('#e47700')
        certificate.text(getLineCoordinateForAttribute(certificate, i18n.t('Certificates.course_format')), startCoordinateForAttributes, getCourseTypesForLearningPath(course.courseCertificates))
    } else {
        certificate.text(i18n.t('Certificates.course_type'), linestartForBottomItems, startCoordinateForAttributes)
        certificate.setTextColor('#e47700')
        certificate.text(getLineCoordinateForAttribute(certificate, i18n.t('Certificates.course_type')), startCoordinateForAttributes, getCourseType(course.courseTypeValue))
    }
    startCoordinateForAttributes += 8
    return certificate
}

function getCourseType (courseType) {
    switch (courseType) {
    case 'micro':
    case 'course':
    case 'wpcourse':
        return i18n.t('Certificates.online_course')
    case 'webinar':
        return i18n.t('Certificates.live_course')
    case 'virtual_classroom':
        return i18n.t('Certificates.digital_classroom')
    case 'podcast':
        return i18n.t('Certificates.podcast')
    default:
        return i18n.t('Certificates.online_course')
    }
}

function getCourseTypesForLearningPath (courseCertificates) {
    const courseTypes = courseCertificates.map(certificate =>
        getCourseType(certificate.courseTypeValue)
    )
    const uniqueCourseTypes = [...new Set(courseTypes)]
    return uniqueCourseTypes.join(', ')
}

function getLineCoordinateForAttribute (certificate, label) {
    return (certificate.getTextWidth(label) + linestartForBottomItems + marginBetweenLabelAndValue)
}

function truncate (text, length) {
    if (text.length < length) {
        return text
    } else {
        return text.substring(0, length) + '...'
    }
}

function isSingleCourse (course) {
    return course.courseIds === undefined || course.courseIds === null || course.courseIds.length === 0
}

function isLearningPath (course) {
    return course.courseIds !== undefined && course.courseIds !== null && course.courseIds.length > 0
}

function convertMinutesToHours (time, unitForHour) {
    const hours = Math.floor(time / 60)
    const minutes = time % 60
    return hours + unitForHour + ' ' + minutes + 'min'
}

function setupNewPdfPage (certificate) {
    certificate.setFont('Nunito-Regular')
    certificate.setTextColor('black')
    let height = 290
    let width = 210
    certificate.addImage(certificateBaseImageSingle.certificateString, 'JPEG', 0, 7, width, height)
    return certificate
}

function addPageNumber (certificate, pageNumber) {
    certificate.setTextColor('black')
    certificate.setFont('Nunito-Bold')
    certificate.setFontSize(13)
    certificate.text(185, 8, i18n.t('CertificateList.page') + ' ' + pageNumber.toString())
    return certificate
}

function addInfoTextForListOfCourses (certificate) {
    certificate.setFontSize(11)
    certificate.setTextColor('black')
    certificate.setFont('Nunito-Italic')
    certificate.text(i18n.t('Certificates.next_page_has_list_of_courses'), 105, 185, 'center')
    return certificate
}

function getNewPdfObject () {
    const certificate = new jsPDF()
    certificate.addFileToVFS('Nunito-Regular-normal.ttf', fontNunitoNormal)
    certificate.addFont('Nunito-Regular-normal.ttf', 'Nunito-Regular', 'normal')
    certificate.addFileToVFS('Nunito-Bold-normal.ttf', fontNunitoBold)
    certificate.addFont('Nunito-Bold-normal.ttf', 'Nunito-Bold', 'normal')
    certificate.addFileToVFS('Quicksand-Bold-normal.ttf', fontQuicksandBold)
    certificate.addFont('Quicksand-Bold-normal.ttf', 'Quicksand-Bold', 'normal')
    certificate.addFileToVFS('Nunito-Italic.ttf', fontNunitoItalic)
    certificate.addFont('Nunito-Italic.ttf', 'Nunito-Italic', 'normal')

    certificate.addFileToVFS('icomoon-normal.ttf', fontIcomoonNormal)
    certificate.addFont('icomoon-normal.ttf', 'icomoon', 'normal')
    return certificate
}

function isDigitalClassRoom (course) {
    return course.entityType === 'course' && course.courseTypeValue === 'virtual_classroom'
}

function setDigitalClassRoomTrainingDate (certificate, course) {
    const digitalClassRoomEvent = store.getters['Courses/getSignedUpEventsForUser'].find(event => course.course.id === event.training.course.id)

    if (digitalClassRoomEvent?.training) {
        certificate.setFontSize(12)
        certificate.setFont('Nunito-Regular')
        certificate.setTextColor('black')
        certificate.text(i18n.t('Certificates.digital_classroom_training_date'), linestartForBottomItems, startCoordinateForAttributes)
        certificate.setTextColor('#e47700')
        const digitalClassRoomTrainingDates = digitalClassRoomEvent.training.sessions.map(session => formattedDateForCountry(session.startDateAndTime)).join(', ')
        certificate.text(getLineCoordinateForAttribute(certificate, i18n.t('Certificates.digital_classroom_training_date')), startCoordinateForAttributes, digitalClassRoomTrainingDates)
    }

    startCoordinateForAttributes += 8
    return certificate
}

function getInstructors (course) {
    return course.instructors.map(i => i.name).join(', ')
}

export default {
    setCertificateLabel,
    setCompletedOnDate,
    setCourseTitle,
    setCourseDescription,
    setUserNameHasCompletedCourse,
    setCourseInstructor,
    setCoursePublishDate,
    setCourseCategories,
    setCourseDuration,
    setAuthorizations,
    setCourseLevel,
    setupNewPdfPage,
    getNewPdfObject,
    resetStartCoordinate,
    setContactInfo,
    setCourseType,
    getCourseType,
    addPageNumber,
    isSingleCourse,
    isLearningPath,
    addInfoTextForListOfCourses,
    isDigitalClassRoom,
    setDigitalClassRoomTrainingDate,
    getInstructors
}
